import * as React from 'react';
import {
    DateField,
    FunctionField,
    Labeled,
    TextField,
    useRecordContext,
    useTranslate,
    SimpleShowLayout, useGetOne, Loading, Identifier, usePermissions
} from 'react-admin';
import {Grid, Link, Typography} from '@mui/material';

import {OrderReturn, Order} from '../../types';
import {statuses} from "../orderStatus";
import {SOURCE} from "../OrderList";
import {hasAccess} from "ra-auth-acl";

const OrderShowGrid = (props: { id: Identifier | undefined }) => {
    const {data: order, isLoading} = useGetOne('orders', {id: props.id}, {staleTime: 30000});
    const t = useTranslate();
    const empty = t(`resources.order.show.none`);
    const {permissions} = usePermissions();
    const hasAccessToOrder = hasAccess(permissions, 'order.list');

    if (isLoading) {
        return <Loading/>;
    }

    return (
        <SimpleShowLayout record={order} sx={{width: "100%"}}>
            <Typography variant="h6" gutterBottom>
                {t('resources.order.show.headline')}
            </Typography>
            <Grid container>
                <Grid item xs={12} sm={6} md={4}>
                    <Labeled
                        source="notifierType"
                        label={`resources.order.show.placed_at`}
                    >
                        <DateField
                            source="placedAt"
                            showTime={true}
                            locales="de-DE"
                            options={{
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            }}
                        />
                    </Labeled>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Labeled source="number" label={`resources.order.number`}>
                        {hasAccessToOrder ? <Link
                            href={`/#/order/${order.id}/show`}
                            target="_blank"
                            rel="noopener"
                        >
                            {order.number.replace(order.source, '')}
                        </Link> : <FunctionField
                            source="number"
                            label="resources.order.number"
                            render={(record: Order) => record.number.replace(record.source, '')}
                        />}
                    </Labeled>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Labeled source="status">
                        <FunctionField
                            source="status"
                            label="resources.order.status"
                            render={(record: Order) =>
                                t(statuses[record.status].translationKey)
                            }
                        />
                    </Labeled>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Labeled
                        source="sentToSelectLineAt"
                        label={`resources.order.show.sent_to_sl_at`}
                    >
                        <DateField
                            source="sentToSelectLineAt"
                            showTime={true}
                            emptyText={empty}
                            locales="de-DE"
                            options={{
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            }}
                        />
                    </Labeled>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Labeled
                        source="source"
                        label={`resources.order.source`}
                    >
                        <FunctionField
                            source="source"
                            label="resources.order.source"
                            render={(record: Order) => SOURCE[record.source] ?? SOURCE.OTHER}
                        />
                    </Labeled>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Labeled
                        source="selectLineDocumentNumber"
                        label={`resources.order.show.sl_order_number`}
                    >
                        <TextField source="selectLineDocumentNumber" emptyText={empty}/>
                    </Labeled>
                </Grid>
            </Grid>
        </SimpleShowLayout>
    );
};


const OrderShow = () => {
    const record = useRecordContext<OrderReturn>();

    if (!record) {
        return <Loading/>;
    }

    return (<OrderShowGrid id={record?.order?.id}/>);
};

export default OrderShow;
